import SvgIcon from '@home/components/SvgIcon';
import { ClassSpecial } from '@op/constants/common';
import useLanguage from '@op/hooks/useLanguage';
import { handleViewMore } from '@op/pages/stats/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Justify, Segment, TabPanel, Tabs } from 'tea-component';
import { useHistory } from 'umi';
import CardCommon from '../CardCommon';
import {
  useRequestComments,
  useRequestLatestEditLog,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import { useSocialMediaColumns } from '../TableCommonHook/hooks/useSocialMediaColumns';
import { SortBy } from 'tea-component/lib/table/addons';
import { LanguageType } from '@op/types/common';
import TableCommonHook from '../TableCommonHook';
import { usePageInfo } from '@op/hooks/common';
import SortBySelect from './SortBySelect';
import { getSortOptions } from '@op/pages/feeds/constants/filter';
import { useGameStoreColumns } from '../TableCommonHook/hooks/useGameStoreColumns';
import NewMediaBox from '@op/pages/feeds/components/NewMediaBox';
import './index.scss';
import { getFeedsStats } from '@op/services';

export type ChannelType = 'comments' | 'social' | 'news';

type TableCommonType = {
  languages: LanguageType[];
  setLanguages: (value: LanguageType[]) => void;
  gameInfo: any;
  wholeGameInfo: any;
  defaultPageInfo: any;
  hasPageable?: boolean;
  tableSticky?: boolean;
  filter: any;
  setSortByOptions: (value: any) => void;
  sortBy: SortBy<any>;
  setSortBy: (value: SortBy<any>) => void;
  isExcludeInvalid: boolean;
  setIsExcludeInvalid: (value: boolean) => void;
};

export default function DetailPageTable({
  hasNews,
  gameInfo,
  defaultPageInfo,
  filter,
  hasPageable,
  tableSticky,
  hasDownload,
  download,
  channelType,
  setChannelType,
  showTotal = false,
  wholeGameInfo,
}: {
  hasNews: boolean;
  gameInfo: any;
  defaultPageInfo: any;
  filter: any;
  hasPageable?: boolean;
  tableSticky?: boolean;
  hasDownload?: boolean;
  download?: (params: any, total: number) => void;
  channelType: ChannelType;
  setChannelType: (value: ChannelType) => void;
  showTotal?: boolean;
  wholeGameInfo: any;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [languages, setLanguages] = useState<LanguageType[]>(['origin']);
  const history = useHistory();
  const [totalMap, setTotalMap] = useState<{
    social: number;
    comments: number;
  }>({
    social: 0,
    comments: 0,
  });

  const [sortByOptions, setSortByOptions] = useState([]);
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'engagement',
    order: 'desc',
  });
  const [isExcludeInvalid, setIsExcludeInvalid] = useState(false);

  const { options, tabs } = useMemo(() => {
    const list = [
      { text: 'Social Media', value: 'social', component: SocialMedia },
      { text: 'Game Store', value: 'comments', component: GameStore },
    ];
    if (hasNews) {
      list.push({ text: 'News Media', value: 'news', component: NewsList });
    }

    return {
      options: list,
      tabs: list.map((item) => ({ label: item.text, id: item.value })),
    };
  }, []);

  useEffect(() => {
    if (showTotal && filter?.edition_unified_id) {
      getFeedsStats({
        ...filter,
        ...gameInfo,
        channel_type: '',
      }).then((data: any) => {
        const { social_total = 0, comments_total = 0 } = data || {};
        setTotalMap({ social: social_total, comments: comments_total });
      });
    }
  }, [filter]);

  return (
    <div className="dbo-detail-page-table-wrapper">
      <Justify
        className="dbo-detail-page-table-header"
        left={
          channelType === 'news' ? null : (
            <SortBySelect
              sortBy={sortBy}
              options={sortByOptions}
              changeSortBy={setSortBy}
              key={'socialMedia'}
            ></SortBySelect>
          )
        }
        right={
          hasDownload ? (
            <Button
              type="icon"
              className="dbo-downloadbutton ns-btn--icon"
              onClick={() => {
                const params: any = {
                  sort_item: sortBy?.by,
                  sort_model: sortBy?.order,
                };
                if (isExcludeInvalid) {
                  params.is_valid = [1, 2, 3];
                }
                download?.(params, totalMap?.[channelType as 'social'] || 0);
              }}
            >
              <SvgIcon type="download" />
            </Button>
          ) : null
        }
      />
      <CardCommon className="dbo-detail-page-table-body">
        <Justify
          className="dbo-stats-feeds-tabs"
          left={
            <Segment
              value={channelType}
              onChange={(value) => {
                setChannelType(value as 'social');
                if (value === 'social') {
                  setSortBy({ by: 'engagement', order: 'desc' });
                } else if (value === 'comments') {
                  setSortBy({ by: 'comment_time', order: 'desc' });
                } else {
                  setSortBy({ by: 'release_time', order: 'desc' });
                }
              }}
              options={
                showTotal
                  ? options.map((item) => {
                      return {
                        ...item,
                        text: (
                          <>
                            {formatMessage({ id: item.text })}(
                            {totalMap[item.value]})
                          </>
                        ),
                      };
                    })
                  : formatOptionsMessage(options)
              }
            />
          }
          right={
            <Button
              type="link"
              className="dbo-stats-view-more"
              onClick={() => {
                handleViewMore(channelType, history);
              }}
            >
              <span>{formatMessage({ id: 'View More' })}</span>
              <SvgIcon type="arrow-right" />
            </Button>
          }
        />

        <Tabs
          tabs={tabs}
          className={ClassSpecial.TAB_NONE}
          activeId={channelType}
        >
          {options.map((item) => {
            return (
              <TabPanel id={item.value} key={item.value}>
                <item.component
                  languages={languages}
                  setLanguages={setLanguages}
                  gameInfo={gameInfo}
                  defaultPageInfo={defaultPageInfo}
                  filter={filter}
                  hasPageable={hasPageable}
                  tableSticky={tableSticky}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  setSortByOptions={setSortByOptions}
                  isExcludeInvalid={isExcludeInvalid}
                  setIsExcludeInvalid={setIsExcludeInvalid}
                  wholeGameInfo={wholeGameInfo}
                ></item.component>
              </TabPanel>
            );
          })}
        </Tabs>
      </CardCommon>
    </div>
  );
}

function SocialMedia({
  languages,
  setLanguages,
  gameInfo,
  defaultPageInfo = { page: 1, page_size: 10 },
  hasPageable = true,
  tableSticky = true,
  filter,
  setSortByOptions,
  sortBy,
  setSortBy,
  isExcludeInvalid,
  setIsExcludeInvalid,
  wholeGameInfo,
}: TableCommonType) {
  const {
    feedsComments,
    feedsCommentsLoading: loading,
    requestComments,
  } = useRequestComments({});

  const { pageInfo, changePageInfo } = usePageInfo({ defaultPageInfo });

  useEffect(() => {
    if (!filter?.edition_unified_id) {
      return;
    }
    const params = {
      ...filter,
      ...pageInfo,
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      channel_type: 'social',
    };

    if (isExcludeInvalid) {
      params.is_valid = [1, 2, 3];
    }

    requestComments(params);
  }, [pageInfo, sortBy, filter, isExcludeInvalid]);

  const { logCommentMap, loading: logLoading } = useRequestLatestEditLog({
    data: (feedsComments?.list || []) as any,
  });

  const { columns, sortColumns, selectSortKeys } = useSocialMediaColumns({
    gameInfo,
    languages,
    sort: sortBy,
    setSort: setSortBy,
    isExcludeInvalid,
    setIsExcludeInvalid,
    isSpecialChannel: false,
    logCommentMap,
    logLoading,
    setLanguages,
    renderKey: '',
    wholeGameInfo
  });

  useEffect(() => {
    const sortKeys = ['important_score', ...selectSortKeys];

    const orderOptions = getSortOptions(sortKeys);

    setSortByOptions(orderOptions);
  }, []);

  return (
    <TableCommonHook
      minWidth={1600}
      recordKey="comment_uin"
      records={feedsComments?.list || []}
      tableSticky={tableSticky}
      sortColumns={sortColumns}
      hasPageable={hasPageable}
      sortInfo={[sortBy, setSortBy]}
      pageInfo={pageInfo}
      columns={columns}
      total={feedsComments?.total || 0}
      loading={loading}
      setPageInfo={changePageInfo}
    />
  );
}

function GameStore({
  languages,
  setLanguages,
  gameInfo,
  defaultPageInfo = { page: 1, page_size: 10 },
  hasPageable = true,
  tableSticky = true,
  filter,
  setSortByOptions,
  sortBy,
  setSortBy,
  isExcludeInvalid,
  setIsExcludeInvalid,
}: TableCommonType) {
  const {
    feedsComments,
    feedsCommentsLoading: loading,
    requestComments,
  } = useRequestComments({});

  const { pageInfo, changePageInfo } = usePageInfo({ defaultPageInfo });

  useEffect(() => {
    if (!filter?.edition_unified_id) {
      return;
    }
    const params = {
      ...filter,
      ...pageInfo,
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      channel_type: 'comments',
    };

    if (isExcludeInvalid) {
      params.is_valid = [1, 2, 3];
    }

    requestComments(params);
  }, [pageInfo, sortBy, filter, isExcludeInvalid]);

  const { logCommentMap, loading: logLoading } = useRequestLatestEditLog({
    data: (feedsComments?.list || []) as any,
  });

  const { columns, sortColumns, selectSortKeys } = useGameStoreColumns({
    gameInfo,
    languages,
    sort: sortBy,
    setSort: setSortBy,
    isExcludeInvalid,
    setIsExcludeInvalid,
    logCommentMap,
    logLoading,
    setLanguages,
    renderKey: '',
  });

  useEffect(() => {
    const sortKeys = [...selectSortKeys];

    if (gameInfo?.edition_unified_id === 'ufc454d9b1af70b40588e2a6fa4da4a8b') {
      sortKeys.unshift('order_number');
    }
    const orderOptions = getSortOptions(sortKeys);
    setSortByOptions(orderOptions);
  }, []);

  return (
    <TableCommonHook
      minWidth={1600}
      recordKey="comment_uin"
      records={feedsComments?.list || []}
      tableSticky={tableSticky}
      sortColumns={sortColumns}
      hasPageable={hasPageable}
      sortInfo={[sortBy, setSortBy]}
      pageInfo={pageInfo}
      columns={columns}
      total={feedsComments?.total || 0}
      loading={loading}
      setPageInfo={changePageInfo}
    />
  );
}

function NewsList({ filter }: { filter: any }) {
  const {
    feedsComments,
    feedsCommentsLoading: loading,
    requestComments,
  } = useRequestComments({});

  useEffect(() => {
    if (!filter?.edition_unified_id) {
      return;
    }
    const params = {
      ...filter,
      sort_item: 'release_time',
      sort_model: 'desc',
      channel_type: 'news',
      page: 1,
      page_size: 5,
    };

    requestComments(params);
  }, [filter]);

  return (
    <CardCommon
      loading={loading}
      empty={feedsComments?.total === 0}
      emptyHeight={300}
      loadingHeight={300}
      contentCard
    >
      {(feedsComments.news_detail_info || []).map((item: any) => {
        return (
          <NewMediaBox
            key={item.comment_uin}
            item={item}
            // language={tableConfigValue.language}
          />
        );
      })}
    </CardCommon>
  );
}
